import { useSyncExternalStore } from "react";

type LoadingStore = {
    ids: Map<string, number>; // ID → Count
    notify: () => void;
};


const globalSubscribers = new Set<() => void>();

const loadingStores = new Map<string, LoadingStore>();

function getOrCreateStore(name: string): LoadingStore {
    if (!loadingStores.has(name)) {
        loadingStores.set(name, {
            ids: new Map(),
            notify: () => {},
        });
    }
    return loadingStores.get(name)!
}

export interface LoadingControl {
    readonly isLoading: boolean;
    isLoadingId: (id: string) => boolean;
    put: (id?: string) => string;
    remove: (id: string) => void;
    withLoading: <T>(fn: () => Promise<T>, loadingId?: string) => Promise<T>;
}

export function useLoadingControl(name: string): LoadingControl {

    const store = getOrCreateStore(name);
    let loadingIdCounter: number = 0;

    const subscribe = (callback: () => void) => {
        store.notify = callback;
        return () => {
            store.notify = () => {};
        };
    };

    const getSnapshot = () => store.ids.size > 0

    const isLoading = useSyncExternalStore(subscribe, getSnapshot)

    const put = (id?: string): string => {

        console.log("loading: put", id)

        const realId = id ?? `id-${++loadingIdCounter}`
        const current = store.ids.get(realId) ?? 0;
        store.ids.set(realId, current + 1);

        store.notify();
        for (const callback of globalSubscribers) {
            callback();
        }

        return realId;
    }

    const isLoadingId = (id: string) => {
        return store.ids.get(id) !== undefined
    }

    const remove = (id: string) => {

        console.log("loading: remove", id)

        const current = store.ids.get(id);
        if (current === undefined) return;

        if (current <= 1) {
            store.ids.delete(id);
        } else {
            store.ids.set(id, current - 1);
        }

        // reduce some flickering
        setTimeout(() => {
            store.notify()
            for (const callback of globalSubscribers) {
                callback()
            }
        }, 250)
    }

    function withLoading<T>(fn: () => Promise<T>): Promise<T>
    function withLoading<T>(fn: () => Promise<T>, loadingId: string): Promise<T>

    async function withLoading<T>(
        fn: () => Promise<T>,
        loadingId?: string,
    ): Promise<T> {
        const id = put(loadingId)
        try {
            return await fn()
        } finally {
            remove(id)
        }
    }

    return { isLoading, isLoadingId, put, remove, withLoading }
}


export function useGlobalLoadingControl(): boolean {

    const subscribe = (callback: () => void) => {
        globalSubscribers.add(callback);
        return () => globalSubscribers.delete(callback);
    };

    const getSnapshot = () => {
        for (const store of loadingStores.values()) {
            if (store.ids.size > 0) return true;
        }
        return false;
    };

    return useSyncExternalStore(subscribe, getSnapshot);
}